/**
 * Chat-history prefetch + in-flight dedup + completed-chat cache.
 *
 * Switching sessions is two sequential network calls (detail REST + signed-URL
 * blob fetch). We warm both on hover so the click path hits cached promises:
 *   - `prefetchChatHistorySession` fires from the rail's onPointerEnter
 *   - `getCachedChatHistoryDetail` and `getCachedBlobJson` are the dedup'd
 *     wrappers consumed by the click path (`handleSelectThread` and
 *     `loadChatSession` respectively)
 *
 * Cache strategy, three layers:
 *   - in-flight promises live 30 s; on error they evict immediately so the
 *     next call retries. The post-stream reconcile path uses raw
 *     `getChatHistoryDetail` (it must see the freshest version), so this cache
 *     won't serve stale data back into that flow.
 *   - transcript blobs at a VERSIONED url (`?v=N`) are immutable by contract
 *     (every persist bumps the session version — chat-history.ts), so they are
 *     kept in a size-capped LRU with no expiry: switching back to a chat never
 *     refetches a transcript it has already seen.
 *   - resolved details are kept per session and served instantly when the
 *     caller can attest the CURRENT version (from the polled rail list) still
 *     matches and no turn is in flight (`getValidatedChatHistoryDetail`) —
 *     switching between completed chats then costs zero requests.
 */
import {
  getChatHistoryDetail,
  type ChatHistoryDetailResponse,
} from "@/lib/apis/web/chat-history-api";

const TTL_MS = 30_000;

interface CacheEntry<T> {
  promise: Promise<T>;
  expiresAt: number;
}

const detailCache = new Map<string, CacheEntry<ChatHistoryDetailResponse>>();
const blobCache = new Map<string, CacheEntry<unknown>>();
/** Immutable versioned transcripts (`?v=N`), LRU on Map insertion order. */
const immutableBlobCache = new Map<string, Promise<unknown>>();
const IMMUTABLE_BLOB_CAP = 40;
/** Last resolved detail per session, validated by version before reuse. */
const resolvedDetailCache = new Map<
  string,
  { version: number; response: ChatHistoryDetailResponse }
>();
const RESOLVED_DETAIL_CAP = 100;

const isVersionedBlobUrl = (url: string): boolean => /[?&]v=\d+/.test(url);

function pruneExpired<T>(cache: Map<string, CacheEntry<T>>): void {
  const now = Date.now();
  for (const [key, entry] of cache) {
    if (entry.expiresAt < now) cache.delete(key);
  }
}

function detailKey(params: {
  sessionId?: string;
  chatSessionId: string;
  projectId?: string;
}): string {
  return [params.sessionId ?? "", params.chatSessionId, params.projectId ?? ""].join(
    "|",
  );
}

export function getCachedChatHistoryDetail(params: {
  sessionId?: string;
  chatSessionId: string;
  projectId?: string;
}): Promise<ChatHistoryDetailResponse> {
  pruneExpired(detailCache);
  const key = detailKey(params);
  const hit = detailCache.get(key);
  if (hit) return hit.promise;

  const promise = getChatHistoryDetail(params);
  detailCache.set(key, { promise, expiresAt: Date.now() + TTL_MS });
  promise.then(
    (response) => {
      const version = response.session.version;
      if (typeof version !== "number") return;
      resolvedDetailCache.delete(params.chatSessionId);
      resolvedDetailCache.set(params.chatSessionId, { version, response });
      if (resolvedDetailCache.size > RESOLVED_DETAIL_CAP)
        resolvedDetailCache.delete(resolvedDetailCache.keys().next().value!);
    },
    () => detailCache.delete(key)
  );
  return promise;
}

/**
 * Detail for a session whose current `version` the caller knows (the polled
 * rail list carries it). A completed chat whose version has not moved is
 * served from memory with no request; anything else — version drift, a turn
 * in flight, or a session never fetched — falls through to the network path.
 */
export function getValidatedChatHistoryDetail(
  params: { sessionId?: string; chatSessionId: string; projectId?: string },
  knownVersion: number | undefined
): Promise<ChatHistoryDetailResponse> {
  const cached = resolvedDetailCache.get(params.chatSessionId);
  if (
    cached &&
    typeof knownVersion === "number" &&
    cached.version === knownVersion &&
    !(cached.response.session as { turnInFlight?: boolean }).turnInFlight
  ) {
    // refresh LRU position
    resolvedDetailCache.delete(params.chatSessionId);
    resolvedDetailCache.set(params.chatSessionId, cached);
    return Promise.resolve(cached.response);
  }
  return getCachedChatHistoryDetail(params);
}

export function getCachedBlobJson(url: string): Promise<unknown> {
  const versioned = isVersionedBlobUrl(url);
  if (versioned) {
    const hit = immutableBlobCache.get(url);
    if (hit) {
      // refresh LRU position
      immutableBlobCache.delete(url);
      immutableBlobCache.set(url, hit);
      return hit;
    }
  } else {
    pruneExpired(blobCache);
    const hit = blobCache.get(url);
    if (hit) return hit.promise;
  }

  const promise = fetch(url).then((response) => {
    if (!response.ok) {
      throw new Error(`Failed to fetch chat transcript (${response.status})`);
    }
    return response.json();
  });
  if (versioned) {
    // A versioned url identifies immutable content (chat-history.ts) — keep it
    // until the LRU cap evicts it, never on a clock.
    immutableBlobCache.set(url, promise);
    if (immutableBlobCache.size > IMMUTABLE_BLOB_CAP)
      immutableBlobCache.delete(immutableBlobCache.keys().next().value!);
    promise.catch(() => immutableBlobCache.delete(url));
  } else {
    blobCache.set(url, { promise, expiresAt: Date.now() + TTL_MS });
    promise.catch(() => blobCache.delete(url));
  }
  return promise;
}

/**
 * Warm the detail + blob caches for an upcoming select. Safe to call repeatedly
 * (in-flight dedup); errors are swallowed because the click path will retry
 * and surface them.
 */
export function prefetchChatHistorySession(params: {
  sessionId?: string;
  chatSessionId: string;
  projectId?: string;
}): void {
  getCachedChatHistoryDetail(params)
    .then((detail) => {
      if (detail.session.messagesBlobUrl) {
        void getCachedBlobJson(detail.session.messagesBlobUrl).catch(() => {
          /* warmed cache will be evicted on error; click path will retry */
        });
      }
    })
    .catch(() => {
      /* swallow — click will surface the real error */
    });
}

/** Evict everything. Use sparingly (e.g., on auth flip). */
export function invalidateChatHistoryPrefetch(): void {
  detailCache.clear();
  blobCache.clear();
  immutableBlobCache.clear();
  resolvedDetailCache.clear();
}
